import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  filesTypes as types,
  LoadPropertyFilesAction,
  LoadPropertyFilesSuccessAction,
  LoadPropertyFilesFailAction,
  LoadPropertyFoldersAction,
  LoadPropertyFoldersSuccessAction,
  LoadPropertyFoldersFailAction,
  CreatePropertyFolderAction,
  CreatePropertyFolderSuccessAction,
  CreatePropertyFolderFailAction,
  RenamePropertyFolderAction,
  RenamePropertyFolderSuccessAction,
  RenamePropertyFolderFailAction,
  MovePropertyFolderAction,
  MovePropertyFolderSuccessAction,
  MovePropertyFolderFailAction,
  DeletePropertyFolderAction,
  DeletePropertyFolderSuccessAction,
  DeletePropertyFolderFailAction,
  RenamePropertyFileAction,
  RenamePropertyFileSuccessAction,
  RenamePropertyFileFailAction,
  MovePropertyFileAction,
  MovePropertyFileSuccessAction,
  MovePropertyFileFailAction,
  DeletePropertyFileAction,
  DeletePropertyFileSuccessAction,
  DeletePropertyFileFailAction,
  AssignComponentsPropertyFileAction,
  AssignComponentsPropertyFileSuccessAction,
  AssignComponentsPropertyFileFailAction,
  DownloadPropertyFileAction,
  DownloadPropertyFileSuccessAction,
  DownloadPropertyFileFailAction,
  BulkMovePropertyFilesAction,
  BulkMovePropertyFilesSuccessAction,
  BulkMovePropertyFilesFailAction,
  BulkDownloadPropertyFilesAction,
  BulkDownloadPropertyFilesSuccessAction,
  BulkDownloadPropertyFilesFailAction,
  BulkDeletePropertyFilesAction,
  BulkDeletePropertyFilesSuccessAction,
  BulkDeletePropertyFilesFailAction,
  RemoveProjectPropertyFileAction,
  RemoveProjectPropertyFileSuccessAction,
  RemoveProjectPropertyFileFailAction,
  UpdateFileProjectAction,
  UpdateFileProjectSuccessAction,
  UpdateFileProjectFailAction,
} from './files.actions';
import * as FileSaver from 'file-saver-es';
import { FileService } from '../shared/services/file.service';
import { BlobWithFilename } from '../../../shared';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { PropertyFile } from '../shared/models';
import { FileDirectory } from '../shared/models/file-directory';
import { getProject } from 'app/internal/projects/projects-store/projects.selectors';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducers';
import * as ProjectActions from '../../projects/projects-store/projects.actions';

@Injectable()
export class FilesEffects {

  constructor(
    private actions$: Actions,
    private fileService: FileService,
    private store: Store<AppState>,
    private toastr: ToastrService
  ) { }

  getPropertyFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_PROPERTY_FILES),
      map(action => (action as LoadPropertyFilesAction).payload),
      mergeMap((payload) =>
        this.fileService
          .getFilesByProperty(payload)
          .pipe(
            map((response: FileDirectory) => new LoadPropertyFilesSuccessAction(response)),
            catchError((error) => of(new LoadPropertyFilesFailAction(error)))
          )
      )
    );
  });

  getPropertyFolders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_PROPERTY_FOLDERS),
      map(action => (action as LoadPropertyFoldersAction).payload),
      mergeMap((payload) =>
        this.fileService
          .getFoldersByProperty(payload)
          .pipe(
            map((response: FileDirectory) => new LoadPropertyFoldersSuccessAction(response)),
            catchError((error) => of(new LoadPropertyFoldersFailAction(error)))
          )
      )
    );
  });

  createPropertyFolder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_PROPERTY_FOLDER),
      map(action => (action as CreatePropertyFolderAction).payload),
      mergeMap((payload) =>
        this.fileService
          .createPropertyFolder(payload)
          .pipe(
            map((response: FileDirectory) => new CreatePropertyFolderSuccessAction(response)),
            catchError((error) => of(new CreatePropertyFolderFailAction(error)))
          )
      )
    );
  });

  createPropertyFolderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_PROPERTY_FOLDER_SUCCESS),
      map(action => (action as CreatePropertyFolderSuccessAction).payload),
      mergeMap((payload: FileDirectory) => [
        new LoadPropertyFoldersAction(payload.propertyId)
      ])
    );
  });

  renamePropertyFolder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.RENAME_PROPERTY_FOLDER),
      map(action => (action as RenamePropertyFolderAction).payload),
      mergeMap((payload) =>
        this.fileService
          .renamePropertyFolder(payload)
          .pipe(
            map((response: FileDirectory) => new RenamePropertyFolderSuccessAction(response)),
            catchError((error) => of(new RenamePropertyFolderFailAction(error)))
          )
      )
    );
  });

  renamePropertyFolderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.RENAME_PROPERTY_FOLDER_SUCCESS),
      map(action => (action as RenamePropertyFolderSuccessAction).payload),
      mergeMap((payload: FileDirectory) => [
        new LoadPropertyFoldersAction(payload.propertyId)
      ])
    );
  });

  movePropertyFolder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.MOVE_PROPERTY_FOLDER),
      map(action => (action as MovePropertyFolderAction).payload),
      mergeMap((payload) =>
        this.fileService
          .movePropertyFolder(payload)
          .pipe(
            map((response: FileDirectory) => new MovePropertyFolderSuccessAction(response)),
            catchError((error) => of(new MovePropertyFolderFailAction(error)))
          )
      )
    );
  });

  movePropertyFolderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.MOVE_PROPERTY_FOLDER_SUCCESS),
      map(action => (action as MovePropertyFolderSuccessAction).payload),
      mergeMap((payload: FileDirectory) => [
        new LoadPropertyFoldersAction(payload.propertyId)
      ])
    );
  });

  deletePropertyFolder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DELETE_PROPERTY_FOLDER),
      map(action => (action as DeletePropertyFolderAction).payload),
      mergeMap((payload) =>
        this.fileService
          .deletePropertyFolder(payload)
          .pipe(
            map(() => new DeletePropertyFolderSuccessAction(payload.propertyId)),
            catchError((error) => of(new DeletePropertyFolderFailAction(error)))
          )
      )
    );
  });

  deletePropertyFolderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DELETE_PROPERTY_FOLDER_SUCCESS),
      map(action => (action as DeletePropertyFolderSuccessAction).payload),
      mergeMap((payload: string) => [
        new LoadPropertyFoldersAction(payload)
      ])
    );
  });

  renamePropertyFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.RENAME_PROPERTY_FILE),
      map(action => (action as RenamePropertyFileAction).payload),
      mergeMap((payload) =>
        this.fileService
          .renamePropertyFile(payload)
          .pipe(
            map((response: PropertyFile) => new RenamePropertyFileSuccessAction(response)),
            catchError((error) => of(new RenamePropertyFileFailAction(error)))
          )
      )
    );
  });

  movePropertyFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.MOVE_PROPERTY_FILE),
      map(action => (action as MovePropertyFileAction).payload),
      mergeMap((payload) =>
        this.fileService
          .movePropertyFile(payload)
          .pipe(
            map((response: PropertyFile) => new MovePropertyFileSuccessAction(response)),
            catchError((error) => of(new MovePropertyFileFailAction(error)))
          )
      )
    );
  });

  deletePropertyFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DELETE_PROPERTY_FILE),
      map(action => (action as DeletePropertyFileAction).payload),
      mergeMap((payload) =>
        this.fileService
          .deletePropertyFile(payload)
          .pipe(
            map((response: string) => new DeletePropertyFileSuccessAction(response)),
            catchError((error) => of(new DeletePropertyFileFailAction(error)))
          )
      )
    );
  });

  assignComponentsPropertyFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.ASSIGN_COMPONENTS_PROPERTY_FILE),
      map(action => (action as AssignComponentsPropertyFileAction).payload),
      mergeMap((payload) =>
        this.fileService
          .assignComponents(payload)
          .pipe(
            map((response: any) => new AssignComponentsPropertyFileSuccessAction(response)),
            catchError((error) => of(new AssignComponentsPropertyFileFailAction(error)))
          )
      )
    );
  });

  downloadPropertyFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DOWNLOAD_PROPERTY_FILE),
      map(action => (action as DownloadPropertyFileAction).payload),
      mergeMap((payload) =>
        this.fileService.downloadPropertyFile(payload)
          .pipe(
            map((response: BlobWithFilename) => new DownloadPropertyFileSuccessAction(response)),
            catchError((error) => of(new DownloadPropertyFileFailAction(error)))
          )
      )
    );
  });

  downloadPropertyFileSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.DOWNLOAD_PROPERTY_FILE_SUCCESS),
      map(action => (action as DownloadPropertyFileSuccessAction).payload),
      tap((blob: BlobWithFilename) => { FileSaver.saveAs(blob, blob.filename); }),
    );
  });

  bulkMovePropertyFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_MOVE_PROPERTY_FILES),
      map(action => (action as BulkMovePropertyFilesAction).payload),
      mergeMap((payload) =>
        this.fileService
          .bulkMovePropertyFiles(payload)
          .pipe(
            map((response: any) => new BulkMovePropertyFilesSuccessAction(response)),
            catchError((error) => of(new BulkMovePropertyFilesFailAction(error)))
          )
      )
    );
  });

  bulkDownloadPropertyFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_DOWNLOAD_PROPERTY_FILES),
      map(action => (action as BulkDownloadPropertyFilesAction).payload),
      mergeMap((payload) =>
        this.fileService
          .bulkDownloadPropertyFiles(payload)
          .pipe(
            map((response: BlobWithFilename) => new BulkDownloadPropertyFilesSuccessAction(response)),
            catchError((error) => of(new BulkDownloadPropertyFilesFailAction(error)))
          )
      )
    );
  });

  bulkDownloadPropertyFileSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_DOWNLOAD_PROPERTY_FILES_SUCCESS),
      map(action => (action as BulkDownloadPropertyFilesSuccessAction).payload),
      tap((blob: BlobWithFilename) => { FileSaver.saveAs(blob, blob.filename); }),
    );
  });

  bulkDeletePropertyFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.BULK_DELETE_PROPERTY_FILES),
      map(action => (action as BulkDeletePropertyFilesAction).payload),
      mergeMap((payload) =>
        this.fileService
          .bulkDeletePropertyFiles(payload)
          .pipe(
            map((response: any) => new BulkDeletePropertyFilesSuccessAction(response)),
            catchError((error) => of(new BulkDeletePropertyFilesFailAction(error)))
          )
      )
    );
  });

  removeProjectPropertyFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.REMOVE_PROJECT_PROPERTY_FILE),
      map(action => (action as RemoveProjectPropertyFileAction).payload),
      mergeMap((payload) =>
        this.fileService
          .removeFromProject(payload.propertyId, payload.propertyFileId, payload.projectId)
          .pipe(
            map((response: any) => new RemoveProjectPropertyFileSuccessAction(response)),
            catchError((error) => of(new RemoveProjectPropertyFileFailAction(error)))
          )
      )
    );
  });

  removeProjectPropertyFileSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.REMOVE_PROJECT_PROPERTY_FILE_SUCCESS),
      map(action => (action as RemoveProjectPropertyFileSuccessAction).payload),
      withLatestFrom(
        this.store.select(getProject)
      ),
      tap(() => {
        this.toastr.success('Project File deleted');
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mergeMap(([payload, project]) => [
        ProjectActions.projectCrudActions.loadProject({ payload: project.projectId }),
      ])
    );
  });

  updateFileProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_FILE_PROJECT),
      map(action => (action as UpdateFileProjectAction).payload),
      mergeMap((payload) =>
        this.fileService
          .updateFileProject(payload)
          .pipe(
            map((response: any) => new UpdateFileProjectSuccessAction(response)),
            catchError((error) => of(new UpdateFileProjectFailAction(error)))
          )
      )
    );
  });
}
